/**
 * The one-time ticket of `/integrations/uspacy/employee-link` (#746, spec
 * `docs/plans/2026-10-29-uspacy-card-tab-session.md` §6, §10.3).
 *
 * The extension opens `…/employee-link?lang=…&org=…#ticket=<token>`. The token
 * rides in the fragment so it never reaches a server, a request log or a
 * Referer. It is read here, at module load, before the router is created and
 * before Sentry starts — the same moment `lib/embedded-session.ts` takes its
 * bootstrap token — and removed from the address bar (`replaceState` also drops
 * it from the history entry).
 *
 * A signed-out reader goes through sign-in first, and Clerk's redirect brings
 * them back WITHOUT the fragment, so the ticket waits in sessionStorage: it
 * survives the redirect in the same tab and dies with the tab. The page forgets
 * it once the link is made or the server says the ticket is dead.
 *
 * Dependency-free: it runs on the synchronous boot path.
 */

const STORAGE_KEY = 'aelo_uspacy_employee_link_ticket';
const PAGE_PATH = '/integrations/uspacy/employee-link';
const FRAGMENT_PARAM = 'ticket';
/** The request schema's bounds (`uspacyEmployeeLinkRequestSchema`). */
const MIN_LENGTH = 32;
const MAX_LENGTH = 256;

/**
 * Where the ticket lives when sessionStorage refuses it. Enough for a reader
 * who is already signed in; a sign-in redirect reloads the page and loses it.
 */
let memoryTicket: string | null = null;

function isTicketShaped(value: string): boolean {
  return value.length >= MIN_LENGTH && value.length <= MAX_LENGTH;
}

/**
 * Move `#ticket=` from the address bar into sessionStorage. No-op off the
 * page or when the fragment carries no ticket. The latest link decides: a
 * ticket that cannot be kept clears the one an earlier link left in this tab.
 * Storage access never breaks boot.
 */
export function captureEmployeeLinkTicket(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.replace(/\/+$/, '') !== PAGE_PATH) return;

  const fragment = new URLSearchParams(window.location.hash.slice(1));
  if (!fragment.has(FRAGMENT_PARAM)) return;
  const ticket = fragment.get(FRAGMENT_PARAM) ?? '';
  fragment.delete(FRAGMENT_PARAM);

  try {
    const rest = fragment.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`
    );
  } catch {
    // The address stays as it is; the ticket is still taken below.
  }

  memoryTicket = isTicketShaped(ticket) ? ticket : null;
  try {
    if (memoryTicket) {
      window.sessionStorage.setItem(STORAGE_KEY, memoryTicket);
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage is blocked: the in-memory copy above is all there is.
  }
}

/**
 * The ticket kept for this tab, or `null`. The in-memory copy goes first: it
 * is set only by THIS load's capture, i.e. by the latest link, while storage may
 * still hold an older ticket if the latest one could not be written (quota).
 * Storage is what remains after a sign-in redirect, which reloads the page.
 */
export function readEmployeeLinkTicket(): string | null {
  if (memoryTicket) return memoryTicket;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/** Forget the ticket — linked, or the server says it is dead. */
export function forgetEmployeeLinkTicket(): void {
  memoryTicket = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to forget in a tab whose storage is blocked.
  }
}

captureEmployeeLinkTicket();
